import { groupBy, map, partition, unionBy } from 'lodash';

import { TEAM_GROUP_ORDER } from 'constants/myTeam';

import { compareMembersByFullname } from 'utils/string';

import type { MyTeamRow, TeamGroup, TeamGroupScope, TeamMember } from '../types';

export const MERGEABLE_RELATION_TYPES = ['accountManager', 'relationshipManager'];

const MERGED_RELATION_TYPE = MERGEABLE_RELATION_TYPES.join('/');

const getScopeKey = (scope?: TeamGroupScope): string | null => {
  if (!scope || scope.name == null) {
    return null;
  }

  return `${scope.type ?? ''}:${scope.id ?? ''}:${scope.name}`;
};

const isMergeableGroup = (group: TeamGroup): boolean => {
  return MERGEABLE_RELATION_TYPES.includes(group?.relationType) && getScopeKey(group?.scope) !== null;
};

const scopeBucketKey = (group: TeamGroup): string => {
  return `${MERGED_RELATION_TYPE}::${getScopeKey(group?.scope)}`;
};

const getMemberIdentityKey = (member: TeamMember): string | undefined => {
  const { user } = member ?? {};

  if (user?.id != null) {
    return `id:${String(user.id)}`;
  }

  if (user?.empId != null) {
    return `empId:${String(user.empId)}`;
  }

  return undefined;
};

/**
 * Merges account/relationship manager groups that share the exact same scope
 * into a single display group. Members are unioned and de-duplicated by user id
 */
export const mergeGroupsForDisplay = (groups: TeamGroup[]): TeamGroup[] => {
  const [mergeableGroups, standaloneGroups] = partition(groups, isMergeableGroup);

  const mergedGroups = map(groupBy(mergeableGroups, scopeBucketKey), (bucket) => ({
    relationType:
      new Set(bucket.map((group) => group.relationType)).size > 1 ? MERGED_RELATION_TYPE : bucket[0].relationType,
    scope: bucket[0].scope,
    members: unionBy(
      bucket.flatMap((group) => group.members ?? []),
      getMemberIdentityKey
    ),
  }));

  return [...standaloneGroups, ...mergedGroups];
};

const getRelationPrecedence = (relationType: string): number | undefined => {
  if (relationType === MERGED_RELATION_TYPE) {
    return TEAM_GROUP_ORDER.indexOf('accountManager');
  }

  const precedence = TEAM_GROUP_ORDER.indexOf(relationType);

  return precedence === -1 ? undefined : precedence;
};

/**
 * Orders the team summary groups by the fixed relation-type precedence and
 * drops any group whose relationType is not part of it.
 */
export const sortAndFilterTeamGroups = (groups: TeamGroup[]): TeamGroup[] => {
  const orderedGroups = groups
    .map((group) => ({ group, precedence: getRelationPrecedence(group?.relationType) }))
    .filter(({ precedence }) => precedence !== undefined)
    .sort((a, b) => a.precedence - b.precedence)
    .map(({ group }) => ({ ...group, members: [...(group.members ?? [])].sort(compareMembersByFullname) }));

  return orderedGroups;
};

/**
 * Updates members with the position they occupy within their group plus the
 * relation type the group is rendered under.
 */
export const withTableMetadata = (members: TeamMember[], relationType: string): MyTeamRow[] => {
  return members.map((member, index) => ({ ...member, rowNumber: index + 1, relationType }));
};

/**
 * Groups the teamMembers(leapfroggers) id by the relation type and maps each
 * of them to a Set of unique member IDs.
 */
export const getMyTeamCount = (groups: TeamGroup[]): Map<string, Set<number>> => {
  const myTeamCounts = new Map();
  const orderedGroups = sortAndFilterTeamGroups(groups);

  orderedGroups.forEach(({ relationType, members }) => {
    const memberIds = members.map((member) => member.user.id);

    if (!myTeamCounts.has(relationType)) {
      myTeamCounts.set(relationType, new Set());
    }

    memberIds.forEach((id) => {
      myTeamCounts.get(relationType).add(id);
    });
  });

  return myTeamCounts;
};
